/**
 * useSectionExitRegeneration
 *
 * Safety-net hook for module pages (CI, SSI, DD, PAD) that ensures AI
 * paragraph regeneration is triggered even when the clinician leaves the
 * page through an "unmanaged" exit path — Breadcrumb, TopNavBar, browser
 * back, or direct URL change — rather than the module's own Next / Prev /
 * Sidebar controls.
 *
 * The hook performs two jobs:
 *   1. On component unmount, fires `TriggerGenerateDocumentSection` if the
 *      page's own navigation handlers did not already handle it.
 *   2. Registers / clears dirty state in `ProformaNavigationGuardContext` so
 *      that the Breadcrumb and TopNavBar can show a confirmation dialog
 *      before the clinician navigates away with unsaved changes.
 *
 * Consumer pages call `markRegenHandled()` at the top of every navigation
 * handler that already triggers regeneration (e.g. handleNextSection,
 * handlePrevSection, navigateToSection).  If the component unmounts and
 * `markRegenHandled` was never called, the cleanup effect fires regeneration.
 *
 * Design constraints:
 *   - All mutable values are stored in refs to avoid stale closures in the
 *     unmount cleanup (the `useEffect` has an empty dependency array).
 *   - Does NOT mark the section as "completed" — that's the responsibility
 *     of `handleSectionCompletion`.  The hook only triggers paragraph
 *     regeneration.
 *   - Explicitly skips the "Patient Information" section (no AI paragraph).
 *   - Accepts an optional `shouldRegenerate` boolean (defaults to true) so
 *     callers like 12_DF.js can skip regeneration for incomplete forms.
 *   - Uses STAGE_TO_REPORT_SECTION to read `stageSectionsGenerated` from
 *     the correct manage-reports section for each module (CI='1', SSI='2',
 *     DD='3', PAD='4').
 *   - Accepts an optional `onUnhandledExit` callback. This fires on the SAME
 *     unhandled-exit path as the CI regeneration (external navigation only,
 *     never on managed Next/Prev/Sidebar exits). It lets a page attach an
 *     extra, self-gated exit side-effect (e.g. the Semble MDT physical-health
 *     paragraph regeneration in CIPrefilledPage) without introducing a second
 *     unmount mechanism. It is purely additive: consumers that don't pass it
 *     (SSI/DD/PAD and every other CI section) are entirely unaffected.
 */
import { useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import { TriggerGenerateDocumentSection } from '../utils/TriggerGenerateDocumentSection';
import { ProformaNavigationGuardContext } from '../context/ProformaNavigationGuardContext';

// Maps stage display titles to their data-model keys so the hook can look up
// `firstCompletedAt` for any module. When SSI, DD, or PAD adopt this hook in
// the future, their stageTitle values already have entries here — no guard
// context changes needed.
const STAGE_TO_MODULE_KEY = {
  'Clinical Interview': '2_clinicalInterview',
  'Semi-Structured Interview': '3_semiStructuredInterview',
  'Diagnostic Decision': '4_diagnosticDecision',
  'Post-Assessment Discussion': '5_postAssessmentDiscussion',
  // The CYP DD/PAD pages are adult copies running over a data bridge that
  // synthesises the same module keys.
  'CYP ADHD Diagnostic Decision': '4_diagnosticDecision',
  'CYP ADHD Post-Assessment Discussion': '5_postAssessmentDiscussion',
};

// Maps stage display titles to their manage-reports section number.
// Each module's generated paragraph outputs, inputs, and
// stageSectionsGenerated live under 6_manageReports.sections[N].
const STAGE_TO_REPORT_SECTION = {
  'Clinical Interview': '1',
  'Semi-Structured Interview': '2',
  'Diagnostic Decision': '3',
  'Post-Assessment Discussion': '4',
  'CYP ADHD Diagnostic Decision': '3',
  'CYP ADHD Post-Assessment Discussion': '4',
};

export function useSectionExitRegeneration({
  patientId,
  stageTitle,
  sectionTitle,
  hasChanges,
  canEdit,
  patientDataModel,
  conditions,
  shouldRegenerate,
  onUnhandledExit,
  forceDirty = false,
  // Set false where the module has no LLM pipeline (CYP PAD: its content
  // feeds the Patient Report, no standalone generation exists) so unmanaged
  // exits do not fire calls the backend guard refuses.
  isGenerationEnabled = true,
}) {
  const regenerationHandled = useRef(false);

  // Determine whether the module that owns this section has been fully
  // completed at least once. This drives whether the navigation guard
  // shows a confirmation dialog (completed → show) or silently navigates
  // (first-time walkthrough → skip dialog, regeneration fires on unmount).
  const moduleCompletedOnce = useMemo(() => {
    const moduleKey = STAGE_TO_MODULE_KEY[stageTitle];
    return moduleKey
      ? !!patientDataModel?.dataModel?.[moduleKey]?.firstCompletedAt
      : false;
  }, [stageTitle, patientDataModel]);

  // Keep a single ref with the latest values so the unmount cleanup
  // always reads current state, regardless of when it runs.
  const latestRef = useRef({
    patientId, stageTitle, sectionTitle, hasChanges, canEdit,
    patientDataModel, conditions, shouldRegenerate, moduleCompletedOnce,
    onUnhandledExit, isGenerationEnabled,
  });
  latestRef.current = {
    patientId, stageTitle, sectionTitle, hasChanges, canEdit,
    patientDataModel, conditions, shouldRegenerate, moduleCompletedOnce,
    onUnhandledExit, isGenerationEnabled,
  };

  // ─── Dirty-state registration in the navigation guard context ────
  const { registerDirty, clearDirty } = useContext(ProformaNavigationGuardContext);

  // `forceDirty` lets a page arm the navigation guard even when the clinician
  // has made no regeneration-relevant edits — e.g. the Diagnostic Decision page
  // forcing the guard on when there is an unresolved SSI decision mismatch so
  // that breadcrumb / top-nav exits are intercepted and re-prompt the dialog.
  // It only affects dirty registration (and the beforeunload warning); the
  // unmount regeneration below still keys on `hasChanges` so a force-dirty with
  // no real edits never triggers a spurious paragraph regeneration.
  useEffect(() => {
    if ((hasChanges && canEdit) || forceDirty) {
      registerDirty(sectionTitle, moduleCompletedOnce, stageTitle);
    }
    return () => clearDirty();
  }, [hasChanges, canEdit, forceDirty, sectionTitle, stageTitle, moduleCompletedOnce, registerDirty, clearDirty]);

  // ─── Mark regeneration as handled ────────────────────────────────
  // Module navigation handlers (handleNextSection, handlePrevSection,
  // navigateToSection) call this before invoking handleSectionCompletion
  // so the unmount cleanup knows NOT to trigger a second regeneration.
  const markRegenHandled = useCallback(() => {
    regenerationHandled.current = true;
  }, []);

  // ─── Reset for intra-route section switches ─────────────────────
  // When the ClinicalInterview container stays mounted across section
  // switches, the unmount cleanup never fires. The section-switch handler
  // calls markRegenHandled() → handleSectionCompletion() → resetRegenHandled()
  // so the flag is ready for the new section. On unmanaged exits (breadcrumb,
  // browser back), the component unmounts with regenerationHandled = false,
  // correctly triggering the safety-net regeneration for the last active section.
  const resetRegenHandled = useCallback(() => {
    regenerationHandled.current = false;
  }, []);

  // ─── Unmount cleanup: fire-and-forget regeneration ───────────────
  useEffect(() => {
    return () => {
      if (regenerationHandled.current) return;

      const {
        patientId: pid,
        stageTitle: stage,
        sectionTitle: section,
        hasChanges: changed,
        canEdit: editable,
        patientDataModel: pdm,
        conditions: conds,
        shouldRegenerate: shouldRegen,
        onUnhandledExit: exitEffect,
      } = latestRef.current;

      if (!editable) return;

      // Additive, opt-in exit side-effect (e.g. Semble MDT physical-health
      // regeneration).  It runs on this unhandled-exit path only — managed
      // exits set regenerationHandled via markRegenHandled and are excluded by
      // the early return above.  It is intentionally independent of the CI
      // regeneration gating below (the consumer self-gates), so the callback
      // fires even when the CI paragraph itself does not need regenerating.
      if (typeof exitEffect === 'function') {
        exitEffect();
      }

      if (shouldRegen === false) return;
      if (latestRef.current.isGenerationEnabled === false) return;
      if (section === 'Patient Information') return;

      const reportSectionKey = STAGE_TO_REPORT_SECTION[stage] || '1';
      const generated = pdm?.dataModel?.['6_manageReports']
        ?.sections?.[reportSectionKey]?.stageSectionsGenerated || [];
      const isGenerated = generated.includes(section);

      if (changed || !isGenerated) {
        TriggerGenerateDocumentSection(pid, stage, section, conds || []);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { markRegenHandled, resetRegenHandled };
}
